import { buildFetchUrl, useFetch, UseFetchProps, UseFetchStatus } from "hooks";
import { useEffect, useCallback, useReducer, useRef } from "react";

type Action = {
  payload?: Array<unknown>;
  type: "FETCH_NEXT_PAGE" | "SUCCESS" | "ERROR" | "RESET_STATE";
};

type State = {
  page: number;
  nextPage: number;
  data: { pages: Array<unknown> };
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
};

const PAGE_SIZE = 25;

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case "FETCH_NEXT_PAGE":
      return {
        ...state,
        isFetchingNextPage: true,
      };
    case "SUCCESS":
      return {
        ...state,
        isFetchingNextPage: false,
        hasNextPage:
          Boolean(action.payload?.length) &&
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          action.payload!.length % PAGE_SIZE === 0,
        data: { pages: [...state.data.pages, ...(action.payload || [])] },
      };
    case "ERROR":
      return {
        ...initialState,
        isFetchingNextPage: false,
        data: { pages: [] },
      };
    case "RESET_STATE":
      return {
        ...initialState,
      };
    default:
      return { ...state };
  }
};

const initialState: State = {
  page: 0,
  nextPage: 1,
  data: { pages: [] },
  isFetchingNextPage: false,
  hasNextPage: true,
};

export interface UseInfiniteFetchResponse<T> {
  hasNextPage: boolean;
  data: { pages: Array<T> };
  fetchNextPage: () => void;
  status: UseFetchStatus;
}

const queryOptions = { throwOnError: true, enabled: false };

export function useInfiniteFetch<T>({
  query,
  url,
  options = { enabled: true },
}: UseFetchProps): UseInfiniteFetchResponse<T> {
  const [state, dispatch] = useReducer(reducer, initialState);
  const queryRef = useRef("");
  const queryInitiallyLoaded = useRef(false);
  const nextPageRef = useRef<number>(1);

  const { refetch, status } = useFetch<Array<T>>({
    query: {},
    url,
    options: queryOptions,
  });

  const fetchNextPage = useCallback(async () => {
    dispatch({ type: "FETCH_NEXT_PAGE" });
    const enhancedQuery = { ...query, page: nextPageRef.current };
    try {
      const res = await refetch(url, enhancedQuery);
      dispatch({ type: "SUCCESS", payload: res });
      nextPageRef.current += 1;
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type: "ERROR", payload: e as any });
    }
  }, [url, query, refetch]);

  useEffect(() => {
    const init = async () => {
      const str = buildFetchUrl(url, query);
      if (
        (queryRef.current === str && queryInitiallyLoaded.current) ||
        !options?.enabled
      ) {
        return;
      }

      // query changed, reset state to start at page one
      // and load the data.
      dispatch({ type: "RESET_STATE" });
      nextPageRef.current = 1;
      queryInitiallyLoaded.current = true;
      queryRef.current = str;
      fetchNextPage();
    };

    init();
  }, [query, url, fetchNextPage, options?.enabled]);

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: state.data as any,
    status: status,
    fetchNextPage,
    hasNextPage: state.hasNextPage,
  };
}
